import { PopInButtonProps } from '@common/types/global.types'
import Button from '@components/arthurBased/inputs/Button'
import Icon from '@components/dataDisplay/Icon'
import { GREY_COLORS, WHITE } from '@config/theme/colors'
import { FontWeight } from '@config/theme/sizes'
import { ArrowBackRounded } from '@mui/icons-material'
import { Modal, Stack, styled, Typography } from '@mui/material'
import { PropsWithChildren } from 'react'

type PopInProps = {
	buttons?: PopInButtonProps[]
	isOpen: boolean
	onClose: () => void
	subtitle?: string
	title: string
}

/**
 * Pop-in
 * @param buttons
 * @param children
 * @param isOpen
 * @param onClose
 * @param subtitle
 * @param title
 * @constructor
 */
const PopIn = (
	{
		buttons = [],
		children,
		isOpen,
		onClose,
		subtitle,
		title
	}: PropsWithChildren<PopInProps>
) => {
	return (
		<Modal open={isOpen}>
			<Container tabIndex={-1}>
				<Header>
					<Title>
						<IconContainer>
							<Icon IconElement={ArrowBackRounded} onClick={onClose} />
						</IconContainer>
						<Typography variant="h4" fontWeight={FontWeight.SEMI_BOLD}>
							{title}
						</Typography>
					</Title>
					{subtitle && (
						<Typography color={GREY_COLORS[300]} variant="body2">
							{subtitle}
						</Typography>
					)}
				</Header>
				<Content>
					{children}
				</Content>
				{!!buttons.length && (
					<Footer>
						{buttons.map((button, index) => (
							<Button
								key={index}
								onClick={button.onClick}
								variant={button.variant}
							>
								{button.label}
							</Button>
						))}
					</Footer>
				)}
			</Container>
		</Modal>
	)
}

const Container = styled(Stack)({
	backgroundColor: WHITE,
	borderRadius: '12px',
	left: '50%',
	maxHeight: '75%',
	minHeight: 0,
	outline: 'none',
	padding: '16px',
	position: 'absolute',
	rowGap: '20px',
	top: '50%',
	transform: 'translate(-50%, -50%)',
	width: '384px',
	'&:focus': {
		outline: 'none'
	},
	'&:focus-visible': {
		outline: 'none'
	}
})

const Header = styled(Stack)({
	rowGap: '8px'
})

const Title = styled(Stack)({
	alignItems: 'center',
	flexDirection: 'row'
})

const IconContainer = styled(Stack)({
	padding: '10px'
})

const Content = styled(Stack)({
	minHeight: 0,
	overflow: 'auto'
})

const Footer = styled(Stack)({
	rowGap: '8px'
})

export default PopIn
